import { debugLogger } from './debugLogger';
import * as Sentry from '@sentry/react';

interface ErrorContext {
  userId?: string;
  vendorId?: string;
  bookingId?: string;
  timestamp?: string;
  [key: string]: unknown;
}

export interface LogEntry {
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error' | 'fatal';
  message: string;
  metadata?: Record<string, unknown>;
}

export interface PerformanceMetric {
  timestamp: string;
  name: string;
  value: number;
  unit: string;
  metadata?: Record<string, unknown>;
}

export interface ErrorReport {
  error: {
    name: string;
    message: string;
    stack?: string;
  };
  context: ErrorContext;
  metadata?: Record<string, unknown>;
}

export interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy';
  errorRate: number;
  recentErrors: number;
  recentLogs: number;
  uptime: number;
}

class MonitoringSystem {
  private enabled = import.meta.env.PROD;
  private logs: LogEntry[] = [];
  private metrics: PerformanceMetric[] = [];
  private errors: ErrorReport[] = [];
  private startTime = Date.now();
  private maxLogEntries = 1000;
  private maxMetricsEntries = 1000;
  private maxErrorEntries = 500;

  captureError(error: Error, context?: ErrorContext) {
    const errorReport: ErrorReport = {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      context: {
        ...context,
        timestamp: new Date().toISOString(),
      },
    };

    // Store error
    this.errors.unshift(errorReport);
    if (this.errors.length > this.maxErrorEntries) {
      this.errors = this.errors.slice(0, this.maxErrorEntries);
    }

    // Also log to debug logger
    debugLogger.error(`Error captured: ${error.message}`, error, context);

    // Send to Sentry if DSN is configured
    const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
    if (sentryDsn && sentryDsn !== 'your-sentry-dsn-here') {
      try {
        Sentry.captureException(error, {
          extra: context,
          tags: {
            userId: context?.userId,
            vendorId: context?.vendorId,
            bookingId: context?.bookingId,
          },
        });
      } catch (sentryError) {
        // Don't let Sentry errors break the app
        console.warn('Failed to send error to Sentry:', sentryError);
      }
    }

    if (!this.enabled) {
      return;
    }
    
    // Log structured error only when enabled
    console.error(JSON.stringify({
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        stack: error.stack,
      },
      context,
    }));
  }

  // Alias for captureError to match Sentry API pattern
  captureException(error: Error, context?: ErrorContext) {
    this.captureError(error, context);
  }

  captureMessage(message: string, level: 'info' | 'warn' | 'error' = 'info') {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
    };

    // Store log
    this.logs.unshift(logEntry);
    if (this.logs.length > this.maxLogEntries) {
      this.logs = this.logs.slice(0, this.maxLogEntries);
    }

    // Also log to debug logger
    if (level === 'error') {
      debugLogger.error(message);
    } else if (level === 'warn') {
      debugLogger.warn(message);
    } else {
      debugLogger.info(message);
    }

    // Send to Sentry for error-level messages
    const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
    if (level === 'error' && sentryDsn && sentryDsn !== 'your-sentry-dsn-here') {
      try {
        Sentry.captureMessage(message, level === 'error' ? 'error' : level === 'warn' ? 'warning' : 'info');
      } catch (sentryError) {
        // Don't let Sentry errors break the app
        console.warn('Failed to send message to Sentry:', sentryError);
      }
    }

    if (!this.enabled) {
      // Only log when disabled for backward compat
      console.log(message);
      return;
    }

    // Only log structured JSON when enabled
    console.log(JSON.stringify({
      timestamp: new Date().toISOString(),
      level,
      message,
    }));
  }

  recordMetric(name: string, value: number, unit: string, metadata?: Record<string, unknown>) {
    const metric: PerformanceMetric = {
      timestamp: new Date().toISOString(),
      name,
      value,
      unit,
      metadata,
    };

    // Store metric
    this.metrics.unshift(metric);
    if (this.metrics.length > this.maxMetricsEntries) {
      this.metrics = this.metrics.slice(0, this.maxMetricsEntries);
    }
  }

  getHealthStatus(): HealthStatus {
    const now = Date.now();
    const fiveMinutesAgo = now - (5 * 60 * 1000);
    
    // Count recent errors (last 5 minutes)
    const recentErrors = this.errors.filter(error => 
      new Date(error.context.timestamp || '').getTime() > fiveMinutesAgo
    ).length;

    // Count recent logs (last 5 minutes)
    const recentLogs = this.logs.filter(log => 
      new Date(log.timestamp).getTime() > fiveMinutesAgo
    ).length;

    // Calculate error rate (errors per minute)
    const errorRate = recentErrors / 5;

    // Determine system status
    let status: 'healthy' | 'degraded' | 'unhealthy';
    if (errorRate === 0) {
      status = 'healthy';
    } else if (errorRate < 2) {
      status = 'degraded';
    } else {
      status = 'unhealthy';
    }

    return {
      status,
      errorRate,
      recentErrors,
      recentLogs,
      uptime: now - this.startTime,
    };
  }

  getLogs(startTime?: Date, limit?: number): LogEntry[] {
    let filteredLogs = this.logs;
    
    if (startTime) {
      filteredLogs = this.logs.filter(log => 
        new Date(log.timestamp) >= startTime
      );
    }

    if (limit) {
      filteredLogs = filteredLogs.slice(0, limit);
    }

    return filteredLogs;
  }

  getMetrics(startTime?: Date, limit?: number): PerformanceMetric[] {
    let filteredMetrics = this.metrics;
    
    if (startTime) {
      filteredMetrics = this.metrics.filter(metric => 
        new Date(metric.timestamp) >= startTime
      );
    }

    if (limit) {
      filteredMetrics = filteredMetrics.slice(0, limit);
    }

    return filteredMetrics;
  }

  getErrors(limit?: number): ErrorReport[] {
    if (limit) {
      return this.errors.slice(0, limit);
    }
    return this.errors;
  }

  // Initialize with some sample data for demonstration
  initializeSampleData() {
    // Add some sample logs
    this.captureMessage('System initialized successfully', 'info');
    this.captureMessage('Database connection established', 'info');
    this.captureMessage('API server started on port 3000', 'info');
    
    // Add some sample metrics
    this.recordMetric('response_time', 150, 'ms');
    this.recordMetric('memory_usage', 256, 'MB');
    this.recordMetric('cpu_usage', 45, '%');
    this.recordMetric('active_connections', 23, 'count');
  }
}

export const monitoring = new MonitoringSystem();

// Initialize with sample data only in development mode
if (import.meta.env.MODE !== 'production') {
  monitoring.initializeSampleData();
}

// Usage in components:
// try {
//   await someOperation();
// } catch (error) {
//   monitoring.captureError(error, { userId, operation: 'booking_create' });
//   throw error;
// }
